// Preschool invoicing: data model, prefill and money maths.
//
// Pure (no Firebase or Node imports) and TS 4.9 compatible: the teacher portal imports it to prefill and preview
// invoices, the functions use it to issue them, and the preschool invoice generator Apps Script bundles it for the
// one-off import of the Costs sheet. Money is integer pence throughout.
//
// One invoice per child. A term invoice has the same lines as the old "Invoices" tab and Google Docs template:
// universal funding (free hours), private childcare, consumable fees on the funded hours, lunch, plus optional
// extra lines (early bird, deposit deduction, ...). Discounts are percentages of the hourly fees only (consumables
// and private hours), as the fees document says.
//
// Two payers: the parent pays the invoice total; the council pays for the funded hours (funded hours x the child's
// funding rate, the sheet's "Funding" columns). The council's money is not on the parent's invoice and arrives as a
// few lump sums per term, so it is reconciled per term (preschoolFundingPayments), not per invoice.

import { childTerm, weeklyPattern, type ChildTerm } from "./preschoolCalendar";

export type PreschoolInvoiceKind = "term" | "registration" | "other";

export const RATES = {
  consumablePence: 280,
  privatePence: 900,
  privateUnderTwoPence: 1000,
  lunchPence: 450,
  depositPence: 5000,
  registrationFeePence: 2500,
};

/** Council hourly funding rates by group (the Costs sheet's CONST tab: Price3yo, Price2yo, Price2yoDis). */
export const FUNDING_RATES = [
  { key: "3-4", label: "3-4 year olds", pence: 566 },
  { key: "2", label: "2 year olds", pence: 822 },
  { key: "2-dis", label: "2 year olds (disadvantaged)", pence: 832 },
];

export const HOURS_PER_SESSION = 3;
export const SIBLING_DISCOUNT_PERCENT = 10;

/** The inputs of a term invoice, as the manager edits them (the "Invoices" tab columns). */
export interface TermCalc {
  funded: {
    weeks: number; hoursPerWeek: number; extraHours: number; consumableRatePence: number; discountPercent: number;
    /** What the council pays per funded hour (not on the parent's invoice). Missing on old documents: treat as 0. */
    fundingRatePence?: number;
  };
  private: { weeks: number; hoursPerWeek: number; extraHours: number; ratePence: number; discountPercent: number };
  lunch: { weeks: number; daysPerWeek: number; extraDays: number; ratePence: number };
}

/** A free line: early bird, deposit returned (negative), registration fee, ... */
export interface ExtraLine {
  description: string;
  quantity: number;
  unitPricePence: number;
}

/** A row as printed on the invoice. */
export interface InvoiceItem {
  description: string;
  quantity: string;
  unitPricePence: number;
  totalPence: number;
}

export interface InvoiceAmounts {
  items: InvoiceItem[];
  subtotalPence: number;
  discountPence: number;
  /** What the parent pays. */
  totalPence: number;
  /** What the council pays for the funded hours (not on the parent's invoice). */
  fundingPence: number;
}

/** What the manager submits to issue (or re-issue) an invoice. */
export interface PreschoolInvoiceInput {
  kind: PreschoolInvoiceKind;
  childId: string;
  termId: string | null;
  /** Shown as "Invoice Period", e.g. "Autumn Term 2026". */
  period: string;
  dueDate: string;
  calc: TermCalc | null;
  extras: ExtraLine[];
}

/** preschoolInvoices/{ref}, e.g. preschoolInvoices/BHP0097 */
export interface PreschoolInvoice extends PreschoolInvoiceInput, InvoiceAmounts {
  ref: string;
  invoiceNo: number;
  childName: string;
  parentName: string;
  address: string;
  email: string;
  issuedDate: string;
  paid: boolean;
  paidDate: string | null;
  cancelled: boolean;
  draftId: string | null;
  draftedAt: string | null;
  draftError: string | null;
  pdfPath: string | null;
  receipt: { draftId: string; paymentDate: string; createdAt: string; pdfPath: string | null } | null;
  /** "spreadsheet": imported from the Costs sheet's Invoices tab (no draft or PDF here). */
  source?: "spreadsheet";
  /** Set when the portal reissued the invoice; a re-run of the sheet import leaves it alone from then on. */
  reissuedAt?: string;
  /** Imported from the sheet but never generated (no PDF in Drive): numbered, not yet sent. Issue it from the portal. */
  notIssued?: boolean;
  /** The old generator's PDF in Drive, for imported invoices. */
  drivePdfId?: string | null;
}

/** preschoolFundingPayments/{id}: one lump sum from the council towards a term's funded hours. */
export interface FundingPayment {
  id: string;
  termId: string;
  date: string;
  amountPence: number;
  note: string;
}

/** preschoolTerms/{id}, e.g. preschoolTerms/2026-autumn. Missing documents fall back to defaultTerm(). */
export interface PreschoolTerm {
  id: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  /** Chargeable weeks (half term excluded). */
  weeks: number;
}

/** The child and registration fields invoicing needs (Firestore children + registrations). */
export interface PreschoolChild {
  id: string;
  name: string;
  parentName: string;
  address: string;
  email: string;
  /** "Monday - Morning, Monday - Afternoon, ..." */
  enrolment: string;
  /** "15 hour funded" | "30 hour funded" | "Not yet entitled" */
  fundingEntitlement: string;
  dateOfBirth: string | null;
  startDate: string | null;
}

// Invoices go out ahead of the term they bill, so August belongs to autumn.
const SEASONS = [
  { key: "spring", name: "Spring", months: [0, 1, 2], weeks: 11 },
  { key: "summer", name: "Summer", months: [3, 4, 5, 6], weeks: 13 },
  { key: "autumn", name: "Autumn", months: [7, 8, 9, 10, 11], weeks: 14 },
];

/** The term an invoice dated on this ISO date bills: Aug-Dec autumn, Jan-Mar spring, Apr-Jul summer. */
export const termIdFor = (isoDate: string) => {
  const year = parseInt(isoDate.slice(0, 4), 10);
  const month = parseInt(isoDate.slice(5, 7), 10) - 1;
  const season = SEASONS.find((s) => s.months.indexOf(month) >= 0)!;
  return `${year}-${season.key}`;
};

/** A term with the usual weeks (38 a year), for terms nobody has configured. */
export const defaultTerm = (id: string): PreschoolTerm => {
  const [year, key] = id.split("-");
  const season = SEASONS.find((s) => s.key === key);
  if (!season || !/^\d{4}$/.test(year)) throw new Error(`Not a term id: ${id}`);
  return { id, name: `${season.name} Term ${year}`, startDate: null, endDate: null, weeks: season.weeks };
};

/** Term ids around a date, newest first: the next term, this one and the previous ones. */
export const recentTermIds = (isoDate: string, previous = 6) => {
  const ids: string[] = [];
  let [year, key] = termIdFor(isoDate).split("-");
  const order = ["spring", "summer", "autumn"];
  let index = order.indexOf(key) + 1;
  let y = parseInt(year, 10);
  if (index > 2) { index = 0; y++; }
  for (let i = 0; i < previous + 2; i++) {
    ids.push(`${y}-${order[index]}`);
    index--;
    if (index < 0) { index = 2; y--; }
  }
  return ids;
};

export const formatRef = (invoiceNo: number) => `BHP${String(invoiceNo).padStart(4, "0")}`;

export const parseRef = (ref: string): number | null => {
  const match = /^BHP(\d+)$/.exec(ref.trim());
  return match ? parseInt(match[1], 10) : null;
};

export const formatPounds = (pence: number) => (pence / 100).toFixed(2);

/** Funded hours per week from the registration form's entitlement text. */
export const entitlementHours = (text: string) => {
  const match = /(\d+)\s*hour/i.exec(text || "");
  return match ? parseInt(match[1], 10) : 0;
};

export const sessionsPerWeek = (enrolment: string) =>
  (enrolment || "").split(",").filter((part) => /-\s*(morning|afternoon)/i.test(part)).length;

/** Morning-only days, where a child may stay for lunch (lunch is included with afternoon sessions). */
export const morningOnlyDays = (enrolment: string) => {
  const days: { [day: string]: { am: boolean; pm: boolean } } = {};
  for (const part of (enrolment || "").split(",")) {
    const [day, time] = part.split("-").map((s) => (s || "").trim().toLowerCase());
    if (!day || !time) continue;
    days[day] = days[day] || { am: false, pm: false };
    if (time === "morning") days[day].am = true;
    if (time === "afternoon") days[day].pm = true;
  }
  return Object.keys(days).filter((day) => days[day].am && !days[day].pm).length;
};

const ageInYears = (dateOfBirth: string, onDate: string) => {
  const born = new Date(dateOfBirth);
  const on = new Date(onDate);
  let age = on.getUTCFullYear() - born.getUTCFullYear();
  if (on.getUTCMonth() < born.getUTCMonth() || (on.getUTCMonth() === born.getUTCMonth() && on.getUTCDate() < born.getUTCDate())) age--;
  return age;
};

const roundHalf = (value: number) => Math.round(value * 2) / 2;

/**
 * A term invoice's inputs from the child's enrolment and funding and, when there is one, the child's previous term
 * invoice (its rates, discounts and lunch pattern carry over). Every value stays editable.
 *
 * With the term's open days from the preschool calendar, the hours are exact: full weeks x hours a week, plus the
 * child's hours on the open days of part-weeks (bank holidays, closures, term edges, a mid-term start) as extra hours,
 * funded up to the entitlement in each week. Without a calendar it falls back to the term's usual weeks.
 */
export const prefillTermCalc = (
  child: PreschoolChild,
  term: PreschoolTerm,
  previous: PreschoolInvoice | null,
  today: string,
  open: string[] | null = null
): { calc: TermCalc; notes: string[] } => {
  const notes: string[] = [];
  const entitlement = entitlementHours(child.fundingEntitlement);
  const pattern = weeklyPattern(child.enrolment, HOURS_PER_SESSION);
  const hoursPerWeek = pattern.hoursByWeekday.reduce((sum, h) => sum + h, 0);
  notes.push(`${sessionsPerWeek(child.enrolment)} sessions a week (${hoursPerWeek} h), ${child.fundingEntitlement || "no funding info"}`);

  let shape: ChildTerm;
  if (open && open.length) {
    shape = childTerm(open, pattern.hoursByWeekday, entitlement, child.startDate, pattern.morningOnly);
    notes.push(`calendar: ${shape.fullWeeks} full weeks` + (shape.partWeeks ? ` + ${shape.partWeeks} part-week${shape.partWeeks === 1 ? "" : "s"} as extra hours` : ""));
    if (child.startDate && child.startDate.slice(0, 10) > open[0]) notes.push(`starts ${child.startDate.slice(0, 10)}`);
  } else {
    let weeks = term.weeks;
    if (child.startDate && term.startDate && term.endDate && child.startDate > term.startDate && child.startDate < term.endDate) {
      const span = Date.parse(term.endDate) - Date.parse(term.startDate);
      weeks = roundHalf((term.weeks * (Date.parse(term.endDate) - Date.parse(child.startDate))) / span);
      notes.push(`Starts ${child.startDate.slice(0, 10)}: ${weeks} of ${term.weeks} weeks`);
    }
    notes.push(`no calendar dates for ${term.name}: usual ${term.weeks} weeks`);
    const funded = Math.min(hoursPerWeek, entitlement);
    shape = {
      fullWeeks: weeks, fundedPerWeek: funded, privatePerWeek: hoursPerWeek - funded, fundedExtra: 0, privateExtra: 0,
      partWeekMorningOnlyDays: 0, morningOnlyPerWeek: pattern.morningOnly.filter(Boolean).length, partWeeks: 0,
    };
  }

  const age = child.dateOfBirth ? ageInYears(child.dateOfBirth, term.startDate || today) : null;
  const underTwo = age !== null && age < 2;
  const prev = previous && previous.calc;
  const fundingRatePence = prev && prev.funded.fundingRatePence
    ? prev.funded.fundingRatePence
    : (age !== null && age < 3 ? FUNDING_RATES[1].pence : FUNDING_RATES[0].pence);
  if (prev) notes.push(`Rates, discounts and lunch as on ${previous!.ref}`);
  if (underTwo && !prev) notes.push("Under two: £10 an hour");

  const lunchPerWeek = prev ? Math.min(prev.lunch.daysPerWeek, shape.morningOnlyPerWeek) : 0;
  const lunchExtra = lunchPerWeek && shape.morningOnlyPerWeek
    ? Math.round((shape.partWeekMorningOnlyDays * lunchPerWeek) / shape.morningOnlyPerWeek)
    : 0;

  return {
    calc: {
      funded: {
        weeks: shape.fundedPerWeek ? shape.fullWeeks : 0,
        hoursPerWeek: shape.fundedPerWeek,
        extraHours: shape.fundedExtra,
        consumableRatePence: prev ? prev.funded.consumableRatePence : RATES.consumablePence,
        discountPercent: prev ? prev.funded.discountPercent : 0,
        fundingRatePence,
      },
      private: {
        weeks: shape.privatePerWeek ? shape.fullWeeks : 0,
        hoursPerWeek: shape.privatePerWeek,
        extraHours: shape.privateExtra,
        ratePence: prev ? prev.private.ratePence : underTwo ? RATES.privateUnderTwoPence : RATES.privatePence,
        discountPercent: prev ? prev.private.discountPercent : 0,
      },
      lunch: {
        weeks: lunchPerWeek ? shape.fullWeeks : 0,
        daysPerWeek: lunchPerWeek,
        extraDays: lunchExtra,
        ratePence: prev ? prev.lunch.ratePence : RATES.lunchPence,
      },
    },
    notes,
  };
};

export const registrationExtras = (): ExtraLine[] => [
  { description: "Deposit fee", quantity: 1, unitPricePence: RATES.depositPence },
  { description: "Registration fee", quantity: 1, unitPricePence: RATES.registrationFeePence },
];

const num = (value: number) => (Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100));

export const termHours = (part: { weeks: number; hoursPerWeek: number; extraHours: number }) =>
  part.weeks * part.hoursPerWeek + part.extraHours;

/** Printed lines and totals. Discounts are rounded to the penny per fee, as the sheet did. */
export const invoiceAmounts = (calc: TermCalc | null, extras: ExtraLine[]): InvoiceAmounts => {
  const items: InvoiceItem[] = [];
  let discountPence = 0;
  let fundingPence = 0;
  if (calc) {
    const fundedHours = termHours(calc.funded);
    const privateHours = termHours(calc.private);
    const lunchDays = calc.lunch.weeks * calc.lunch.daysPerWeek + calc.lunch.extraDays;
    const privateTotal = Math.round(privateHours * calc.private.ratePence);
    const consumablesTotal = Math.round(fundedHours * calc.funded.consumableRatePence);
    items.push({ description: "Universal Funding", quantity: `${num(fundedHours)} hours`, unitPricePence: 0, totalPence: 0 });
    items.push({ description: "Private childcare", quantity: `${num(privateHours)} hours`, unitPricePence: calc.private.ratePence, totalPence: privateTotal });
    items.push({
      description: "Consumable fees (inc French classes, Woodlands school)",
      quantity: `${num(fundedHours)} hours`,
      unitPricePence: calc.funded.consumableRatePence,
      totalPence: consumablesTotal,
    });
    items.push({ description: "Lunch", quantity: `${num(lunchDays)} days`, unitPricePence: calc.lunch.ratePence, totalPence: Math.round(lunchDays * calc.lunch.ratePence) });
    discountPence += Math.round((consumablesTotal * calc.funded.discountPercent) / 100);
    discountPence += Math.round((privateTotal * calc.private.discountPercent) / 100);
    fundingPence = Math.round(fundedHours * (calc.funded.fundingRatePence || 0));
  }
  for (const extra of extras) {
    items.push({
      description: extra.description,
      quantity: num(extra.quantity),
      unitPricePence: extra.unitPricePence,
      totalPence: Math.round(extra.quantity * extra.unitPricePence),
    });
  }
  const subtotalPence = items.reduce((sum, item) => sum + item.totalPence, 0);
  return { items, subtotalPence, discountPence, totalPence: subtotalPence - discountPence, fundingPence };
};

/** Problems that must be fixed before an invoice can be issued; empty when it is fine. */
export const validateInput = (input: PreschoolInvoiceInput): string[] => {
  const problems: string[] = [];
  const nonNegative = (label: string, value: number) => {
    if (!(typeof value === "number" && isFinite(value) && value >= 0)) problems.push(`${label} must be zero or more.`);
  };
  if (!input.childId) problems.push("Choose a child.");
  if (!input.period.trim()) problems.push("Invoice period is required.");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.dueDate)) problems.push("Due date is required.");
  if (input.calc) {
    const { funded, lunch } = input.calc;
    const priv = input.calc.private;
    [["Funded weeks", funded.weeks], ["Funded hours a week", funded.hoursPerWeek], ["Extra funded hours", funded.extraHours],
      ["Consumable fee", funded.consumableRatePence], ["Funding rate", funded.fundingRatePence ?? 0], ["Private weeks", priv.weeks], ["Private hours a week", priv.hoursPerWeek],
      ["Extra private hours", priv.extraHours], ["Hourly rate", priv.ratePence], ["Lunch weeks", lunch.weeks],
      ["Lunch days a week", lunch.daysPerWeek], ["Extra lunch days", lunch.extraDays], ["Lunch fee", lunch.ratePence],
    ].forEach(([label, value]) => nonNegative(label as string, value as number));
    for (const [label, value] of [["Consumables discount", funded.discountPercent], ["Private discount", priv.discountPercent]] as [string, number][]) {
      if (!(value >= 0 && value <= 100)) problems.push(`${label} must be between 0 and 100%.`);
    }
  }
  for (const extra of input.extras) {
    if (!extra.description.trim()) problems.push("Every extra line needs a description.");
    if (!(isFinite(extra.quantity) && isFinite(extra.unitPricePence))) problems.push(`"${extra.description}" needs a number.`);
  }
  if (!input.calc && input.extras.length === 0) problems.push("Add at least one line.");
  return problems;
};

/** Fields of Firestore children/{id} and registrations/{id} that invoicing reads. */
export interface ChildRecord { id: string; registrationId?: string; parentEmail?: string; ignore?: boolean }
export interface RegistrationRecord {
  childFirstName?: string; childMiddleName?: string; childLastName?: string;
  parent1FullName?: string; parent1Address?: string; parent1Email?: string; email?: string;
  enrolment?: string; fundingEntitlement?: string; childDateOfBirth?: unknown; startDate?: unknown;
}

const dateText = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  const timestamp = value as { toDate?: () => Date; seconds?: number };
  if (typeof timestamp.toDate === "function") return timestamp.toDate().toISOString();
  if (typeof timestamp.seconds === "number") return new Date(timestamp.seconds * 1000).toISOString();
  return null;
};

export const toPreschoolChild = (child: ChildRecord, registration: RegistrationRecord | undefined): PreschoolChild => {
  const r = registration || {};
  const name = [r.childFirstName, r.childMiddleName, r.childLastName].map((part) => (part || "").trim()).filter(Boolean).join(" ");
  return {
    id: child.id,
    name: name || child.id,
    parentName: (r.parent1FullName || "").trim(),
    address: (r.parent1Address || "").trim(),
    email: (r.parent1Email || r.email || child.parentEmail || "").trim(),
    enrolment: r.enrolment || "",
    fundingEntitlement: r.fundingEntitlement || "",
    dateOfBirth: dateText(r.childDateOfBirth),
    startDate: dateText(r.startDate),
  };
};
